import { Head, Link } from "@inertiajs/react"

import PlatformIcon from "@/components/icons/platform-icon"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/format"
import { storyPath } from "@/routes"
import type { BreadcrumbItem, Syndication } from "@/types"

export default function SyndicationRepliesIndex({
  story_id,
  syndication,
}: {
  story_id: number
  syndication: Syndication
}) {
  const platformLabel = syndication.platform.label
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Stories", href: "/stories" },
    { title: "Story", href: storyPath(story_id) },
    { title: `${platformLabel} Replies`, href: "#" },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${platformLabel} Replies`} />

      <div className="mx-auto w-full max-w-2xl px-4 py-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="bg-muted text-foreground flex size-10 shrink-0 items-center justify-center rounded-full">
            <PlatformIcon
              platform={syndication.platform.name}
              className="size-5"
            />
          </div>
          <div>
            <h1 className="text-lg font-semibold">{platformLabel} Replies</h1>
            <p className="text-muted-foreground text-sm">
              {syndication.reply_count}{" "}
              {syndication.reply_count === 1 ? "reply" : "replies"}
            </p>
          </div>
        </div>

        {syndication.replies.length === 0 ? (
          <p className="text-muted-foreground text-sm">No replies yet.</p>
        ) : (
          <div className="space-y-3">
            {syndication.replies.map((reply) => (
              <div
                key={reply.id}
                className="rounded-lg border bg-white/60 p-4 dark:bg-zinc-900/50"
              >
                <div className="flex items-start gap-3">
                  {reply.author_avatar_url ? (
                    <img
                      src={reply.author_avatar_url}
                      alt=""
                      className="mt-0.5 size-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="bg-muted size-8 rounded-full" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                      <span className="font-medium">{reply.author_name}</span>
                      {reply.author_handle && (
                        <span className="text-muted-foreground">
                          @{reply.author_handle}
                        </span>
                      )}
                      <span className="text-muted-foreground">
                        {formatDate(reply.posted_at, true)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm whitespace-pre-wrap">
                      {reply.body}
                    </p>
                    {reply.external_url && (
                      <a
                        href={reply.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex text-sm underline underline-offset-4"
                      >
                        View reply
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6">
          <Link
            href={storyPath(story_id)}
            className="text-sm underline underline-offset-4"
          >
            &larr; Back to story
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
